import { MouseEvent, ReactNode } from "react";
import { useSelector } from "react-redux";
import { Box, Link, Typography } from "@mui/material";
import { RootState } from "@store/store";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import useInterfaceConfigV2 from "@hooks/useInterfaceConfigV2";
import { formatCompanyList } from "@helpers/funciones";
import { TAX_SYSTEM_TYPE } from "@helpers/constantes";
import { TextoLegalSeccion } from "@interfaces/catalogs";
import { LinkTerminosPrivacidad } from "./LinkTerminosPrivacidad";

/** Textos legales del wizard v2. Al agregar uno, se suma aquí y en `LEGAL_TEXTS`. */
export type LegalTextType = "autorizacionSat" | "autorizacionBuro" | "avisoEvaluacion" | "modalBuro";

/** Razón social si la marca no tiene `companyNames` en su config (la de Lendia, como `ENT()` del diseño). */
const DEFAULT_COMPANIES = "Lendit Group SAPI de CV";

/** Párrafo dentro de un texto legal de varios párrafos (el componente es en línea, así que va como `span`). */
const Parrafo = ({ children, first }: { children: ReactNode; first?: boolean }) => (
  <Box component="span" sx={{ display: "block", mt: first ? 0 : 2 }}>
    {children}
  </Box>
);

/** Datos de la solicitud y de la marca que pueden usar los textos. */
interface LegalTextContext {
  /** Razones sociales de la marca (`companyNames` de su config), unidas con comas y "y". */
  companies: string;
  /** Régimen de la solicitud: "PM", "PFAE" o "PF". */
  taxSystemType: string;
}

/**
 * Texto de cada tipo. Si cambia por régimen, se decide aquí con `taxSystemType`; si cambia por empresa, con
 * `companies` (o agregando el dato que haga falta al contexto). La pantalla solo elige el `type`.
 */
const LEGAL_TEXTS: Record<LegalTextType, (context: LegalTextContext) => ReactNode> = {
  // Check de la autorización del SAT (CIEC) en `ConexionSAT` (`SAT_CHECK` del diseño).
  autorizacionSat: ({ companies }) => (
    <>
      Autorizo a {companies} a consultar mi información fiscal ante el SAT mediante mi Contraseña (CIEC), bajo los{" "}
      <LinkTerminosPrivacidad termsLabel="términos y condiciones" connector="y el" />.
    </>
  ),
  // Check de la autorización de Buró en `ConexionSAT` (`BURO_CHECK` del diseño). Un texto por tipo de persona; si
  // llega un régimen sin texto propio, se usa el de PM.
  autorizacionBuro: ({ companies, taxSystemType }) => {
    const porRegimen: Record<string, ReactNode> = {
      [TAX_SYSTEM_TYPE.PFAE.code]: (
        <>
          Autorizo a {companies} a consultar mi información ante Sociedades de Información Crediticia, como persona
          física y como persona física con actividad empresarial.
        </>
      ),
      // TODO: confirmar texto de PM (hoy el del diseño, `BURO_CHECK('PM')`).
      [TAX_SYSTEM_TYPE.PM.code]: <>Autorizo a {companies} a consultar mi información ante Buró de Crédito.</>,
      // TODO: confirmar texto de PF (hoy el del diseño, `BURO_CHECK('PF')`).
      [TAX_SYSTEM_TYPE.PF.code]: <>Autorizo a {companies} a consultar mi información ante Buró de Crédito.</>,
    };
    return porRegimen[taxSystemType] ?? porRegimen[TAX_SYSTEM_TYPE.PM.code];
  },
  // Modal de la autorización de Buró (`BuroLegal` del diseño), en `ConexionSAT` y `NipBuro`. Un texto por tipo de persona; si llega un
  // régimen sin texto propio, se usa el de PF.
  modalBuro: ({ companies, taxSystemType }) => {
    const porRegimen: Record<string, ReactNode> = {
      // Prototipo de Mercader.
      [TAX_SYSTEM_TYPE.PFAE.code]: (
        <>
          <Parrafo first>
            Por este conducto autorizo expresamente a {companies} para que por conducto de sus funcionarios facultados
            lleven a cabo investigaciones sobre el comportamiento crediticio de la Empresa que represento en Dun &
            Bradstreet, S.A. SIC.
          </Parrafo>
          <Parrafo>
            Así mismo, declaro que conozco la naturaleza y alcance de la información que se solicitará, del uso que{" "}
            {companies} hará de tal información y de que ésta podrá realizar consultas periódicas de mi historial
            crediticio, consintiendo que esta autorización se encuentre vigente por un periodo de 3 años contados a
            partir de la fecha de su expedición y en todo caso durante el tiempo que mantengamos relación jurídica.
          </Parrafo>
          <Parrafo>
            Declaro bajo protesta de decir verdad que soy el titular de la información objeto de esta autorización y que
            la otorgo por mi propio derecho.
          </Parrafo>
        </>
      ),
      // TODO: confirmar texto de PM (hoy el de `CustomTextCiecBuro`).
      [TAX_SYSTEM_TYPE.PM.code]: (
        <>
          <Parrafo first>
            Por este conducto autorizo expresamente a {companies} para que por conducto de sus funcionarios facultados
            lleven a cabo investigaciones sobre el comportamiento crediticio de la Empresa que represento en Dun &
            Bradstreet, S.A. SIC.
          </Parrafo>
          <Parrafo>
            Asimismo, declaro que conozco la naturaleza y alcance de la información que se solicitará, del uso que{" "}
            {companies} harán de tal información y de que éstas podrán realizar consultas periódicas de mi historial
            crediticio, consintiendo que esta autorización se encuentre vigente por un periodo de 3 años contados a
            partir de la fecha de su expedición y en todo caso durante el tiempo que mantengamos relación jurídica.
          </Parrafo>
          <Parrafo>
            Declaro bajo protesta de decir verdad ser Representante Legal de la empresa mencionada en esta autorización;
            manifestando que a la fecha de la presente autorización los poderes no me han sido revocados, ni limitados
            en forma alguna.
          </Parrafo>
        </>
      ),
      // Diseño (`BuroLegal('PF')`): también el del accionista en `ConsultaBuro`.
      [TAX_SYSTEM_TYPE.PF.code]: (
        <>
          <Parrafo first>
            Por este conducto autorizo expresamente a {companies} para que por conducto de sus funcionarios facultados
            lleven a cabo investigaciones sobre mi comportamiento crediticio en Trans Union de México, S.A. SIC.
          </Parrafo>
          <Parrafo>
            Asimismo, declaro que conozco la naturaleza y alcance de la información que se solicitará, del uso que{" "}
            {companies} harán de tal información y de que éstas podrán realizar consultas periódicas de mi historial
            crediticio, consintiendo que esta autorización se encuentre vigente por un periodo de 3 años contados a
            partir de la fecha de su expedición y en todo caso durante el tiempo que mantengamos relación jurídica.
          </Parrafo>
          <Parrafo>
            Declaro bajo protesta de decir verdad que soy el titular de la información objeto de esta autorización y que
            la otorgo por mi propio derecho.
          </Parrafo>
        </>
      ),
    };
    return porRegimen[taxSystemType] ?? porRegimen[TAX_SYSTEM_TYPE.PF.code];
  },
  // Aviso debajo de las autorizaciones en `ConexionSAT` (`BURO_AVISO` del diseño).
  avisoEvaluacion: ({ companies }) => (
    <>
      Realizaremos una evaluación que nos determinará la factibilidad de poder acceder a financiamiento. Al continuar,
      el solicitante forma una relación jurídica como cliente de {companies} y las autoriza a realizar consultas y
      evaluar información por mecanismos digitales.
    </>
  ),
};

interface Props {
  type: LegalTextType;
  /**
   * Régimen con el que se arma el texto, si no es el de la solicitud. P. ej. el accionista de una PM autoriza
   * como persona física (`TAX_SYSTEM_TYPE.PF.code`).
   */
  taxSystemType?: string;
  /**
   * Texto de la empresa (config `TEXTOS_LEGALES`). Si viene, reemplaza al texto por defecto de `type`; los enlaces
   * van como `[texto visible](destino)`.
   */
  texto?: string;
  /** Igual que `texto`, para los de varios párrafos (modales). Tiene prioridad sobre `texto`. */
  parrafos?: string[];
  /** Párrafos agrupados en apartados con subtítulo. Tiene prioridad sobre `parrafos` y `texto`. */
  secciones?: TextoLegalSeccion[];
}

/** Marcas de un texto configurable: `[texto](destino)` (enlace) o `**texto**` (razón social resaltada, `⟦…⟧` del diseño). */
const MARCAS = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g;

/** Abre en otra pestaña sin marcar el check en cuyo label va el texto. */
const stopPropagation = (event: MouseEvent) => event.stopPropagation();

/**
 * Texto configurable con sus marcas: enlaces (`terminos` y `aviso_privacidad` van a las URLs de la marca) y razones
 * sociales resaltadas en el color de marca, sin ser enlace.
 */
const TextoConEnlaces = ({ texto }: { texto: string }) => {
  const interfaceConfigV2 = useInterfaceConfigV2();
  const urls: Record<string, string> = {
    terminos: interfaceConfigV2?.url_terms_and_conditions || "/terminos_y_condiciones",
    aviso_privacidad: interfaceConfigV2?.url_privacy_notice || "/aviso_de_privacidad",
  };

  const partes: ReactNode[] = [];
  let desde = 0;
  for (const match of texto.matchAll(MARCAS)) {
    const [completo, etiqueta, destino, resaltado] = match;
    const inicio = match.index ?? 0;
    if (inicio > desde) partes.push(texto.slice(desde, inicio));
    desde = inicio + completo.length;
    if (resaltado) {
      // `font-medium text-brand-text` del diseño: mismo color que los enlaces, sin serlo.
      partes.push(
        <Typography key={inicio} component="span" sx={{ font: "inherit", fontWeight: 500, color: "primary.main" }}>
          {resaltado}
        </Typography>,
      );
      continue;
    }
    partes.push(
      <Link
        key={inicio}
        href={urls[destino] ?? destino}
        target="_blank"
        rel="noopener noreferrer"
        onClick={stopPropagation}
        color="primary.main"
        data-testid={`link-texto-legal-${destino}`}
      >
        {etiqueta}
      </Link>,
    );
  }
  if (desde < texto.length) partes.push(texto.slice(desde));

  return <>{partes}</>;
};

/**
 * Textos legales del v2 (autorizaciones de CIEC, Buró, etc.). Reemplaza a `CustomTextCiecBuro` para las pantallas
 * nuevas: mismos datos (razones sociales de la marca y régimen de la solicitud), con los textos en un solo mapa.
 * Con `texto` (config de la empresa) se muestra ese en lugar del de `type`.
 */
export const TypographyLegal = ({ type, taxSystemType: regimen, texto, parrafos, secciones }: Props) => {
  const interfaceConfig = useInterfaceConfig();
  const regimenSolicitud = useSelector((state: RootState) => state.register.application.tax_system_type);
  const taxSystemType = regimen ?? regimenSolicitud;

  const parrafosConfig = parrafos?.filter((parrafo) => parrafo?.trim()) ?? [];
  const seccionesConfig = secciones?.filter((seccion) => seccion?.parrafos?.some((parrafo) => parrafo?.trim())) ?? [];

  const content = seccionesConfig.length ? (
    seccionesConfig.map((seccion, indexSeccion) => (
      <Box key={indexSeccion} component="span" sx={{ display: "block", mt: indexSeccion === 0 ? 0 : 3 }}>
        {seccion.subtitulo?.trim() && (
          // `Sub` del diseño: letra chica en mayúsculas, en el color de los títulos.
          <Typography
            component="span"
            variant="body3"
            sx={{
              display: "block",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "primary.dark",
              mb: 1,
            }}
          >
            {seccion.subtitulo}
          </Typography>
        )}
        {seccion.parrafos
          .filter((parrafo) => parrafo?.trim())
          .map((parrafo, index) => (
            <Parrafo key={index} first={index === 0}>
              <TextoConEnlaces texto={parrafo} />
            </Parrafo>
          ))}
      </Box>
    ))
  ) : parrafosConfig.length ? (
    parrafosConfig.map((parrafo, index) => (
      <Parrafo key={index} first={index === 0}>
        <TextoConEnlaces texto={parrafo} />
      </Parrafo>
    ))
  ) : texto?.trim() ? (
    <TextoConEnlaces texto={texto} />
  ) : (
    LEGAL_TEXTS[type]({
      companies: formatCompanyList(interfaceConfig?.companyNames ?? [], false) || DEFAULT_COMPANIES,
      taxSystemType,
    })
  );

  if (!content) return null;

  // En línea y con el estilo del contenedor: los textos van dentro de labels de checks o de párrafos.
  return (
    <Typography component="span" sx={{ font: "inherit", color: "inherit" }}>
      {content}
    </Typography>
  );
};
